/**
 * Shop Promise (delivery-promise-wc) — inject overrides into open shadow root.
 * Internal UI lives inside #shadow-root; theme CSS cannot target those classes.
 */
(function () {
  const STYLE_ID = 'ez-shop-promise-overrides';
  const ROW_CLASS = 'ez-shop-promise-row';
  const TOGGLE_CLASS = 'ez-shop-promise-toggle';
  const COPY_CLASS = 'ez-shop-promise-copy';
  const DETAIL_CLASS = 'ez-shop-promise-detail';
  const TOGGLE_PAD_CLASS = 'ez-shop-promise-toggle__pad';
  const ROW_GAP = '6px';
  const PIN_SVG =
    '<svg aria-hidden="true" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">' +
    '<path d="M6.5 10.5C6.5 7 9 4.5 12.5 4.5C16 4.5 18.5 7 18.5 10.5C18.5 14 15.5 17.5 12.5 20.5C9.5 17.5 6.5 14 6.5 10.5Z" stroke="currentColor" stroke-width="1.2"/>' +
    '<path d="M14 10.5C14 11.3284 13.3284 12 12.5 12C11.6716 12 11 11.3284 11 10.5C11 9.67157 11.6716 9 12.5 9C13.3284 9 14 9.67157 14 10.5Z" stroke="currentColor" stroke-width="1.2"/>' +
    '</svg>';

  const CSS = `
    .ShadowDOMStylesBoundary__Container {
      padding: 0 !important;
      margin: 0 !important;
      border-radius: 0 !important;
      background-color: transparent !important;
      color: rgb(var(--ez-color-primary, 123, 157, 73)) !important;
    }

    .ProductPageContainer__Spacer {
      padding: 0 !important;
      margin: 0 !important;
    }

    .ContainerCommon__Container,
    .PromiseShell__ShopPromise-SmoothContainer,
    .PromiseShell__Content,
    .ShopPromiseLayout__Clickable,
    .ExpansionBlock__ExpansionBlock {
      padding: 0 !important;
      margin: 0 !important;
      border-radius: 0 !important;
      background-color: transparent !important;
      color: rgb(var(--ez-color-primary, 123, 157, 73)) !important;
      font-size: inherit !important;
      letter-spacing: inherit !important;
      line-height: inherit !important;
    }

    .ShadowDOMStylesBoundary__Container :is(p, span, a, label) {
      color: rgb(var(--ez-color-primary, 123, 157, 73)) !important;
      font-size: inherit !important;
      letter-spacing: inherit !important;
      line-height: inherit !important;
    }

    input,
    button {
      border-radius: 0 !important;
    }

    .ShopPromiseLayout__Clickable:hover,
    .ShopPromiseLayout__Clickable:active,
    .ShopPromiseLayout__Clickable:focus,
    .ShopPromiseLayout__Clickable:focus-visible {
      background-color: transparent !important;
    }

    .${ROW_CLASS} {
      display: flex !important;
      align-items: center !important;
      column-gap: ${ROW_GAP} !important;
      --ui-inline-stack-column-gap: ${ROW_GAP} !important;
      --ui-inline-stack-row-gap: 0 !important;
    }

    .${TOGGLE_CLASS} {
      flex: 0 0 auto;
      display: inline-flex;
      align-items: center;
      margin: 0;
      padding: 0;
      line-height: 0;
      color: rgb(var(--ez-color-primary, 123, 157, 73));
    }

    .${TOGGLE_CLASS} svg {
      display: block;
      width: calc(var(--font-heading-scale, 1) * 2rem * var(--ez-shop-promise-icon-scale, 1.4));
      height: calc(var(--font-heading-scale, 1) * 2rem * var(--ez-shop-promise-icon-scale, 1.4));
    }

    .${TOGGLE_CLASS} svg path {
      fill: none;
      stroke: currentColor;
      stroke-width: 1.2;
      stroke-linecap: round;
      stroke-linejoin: round;
    }

    .${TOGGLE_PAD_CLASS} {
      flex: 0 0 ${ROW_GAP};
      width: ${ROW_GAP};
      min-width: ${ROW_GAP};
      height: 1px;
      display: inline-block;
      margin: 0;
      padding: 0;
      border: 0;
    }

    .${COPY_CLASS} {
      min-width: 0;
      flex: 1 1 auto;
      margin: 0;
      padding: 0;
    }

    .${DETAIL_CLASS} {
      box-sizing: border-box !important;
      padding-inline-start: var(--ez-shop-promise-text-indent, 0) !important;
    }

    .${ROW_CLASS} [class*="Chevron"],
    .${ROW_CLASS} [class*="Caret"] {
      display: none !important;
    }

    .ShadowDOMStylesBoundary__Container,
    .ShadowDOMStylesBoundary__Container :is(p, span, a, label, div) {
      text-transform: uppercase !important;
      font-size: inherit !important;
      letter-spacing: inherit !important;
      line-height: inherit !important;
    }

    input {
      text-transform: none !important;
    }

    p[data-ez-hide-guarantee] {
      display: none !important;
    }

    [class*="TextField"] [class*="Icon"],
    [class*="TextField"] [class*="Prefix"],
    [class*="TextField"] svg,
    [class*="Prefix"] svg,
    [class*="InlineStack"]:has(input) > svg,
    [class*="InlineStack"]:has(input) > [class*="Icon"],
    [class*="InlineStack"]:has(input) > [class*="Prefix"],
    [class*="Stack"]:has(input) > svg,
    [class*="Stack"]:has(input) > [class*="Icon"],
    *:has(> input):not(button) > svg,
    *:has(> input):not(button) > [class*="Icon"] {
      display: none !important;
      width: 0 !important;
      min-width: 0 !important;
      height: 0 !important;
      overflow: hidden !important;
      margin: 0 !important;
      padding: 0 !important;
    }
  `;

  function isDefaultRowIcon(child, toggle) {
    if (child === toggle) return false;

    var className = String(child.className || '');
    var text = (child.textContent || '').replace(/\s+/g, ' ').trim();
    var hasSvg = !!child.querySelector('svg');

    if (text.length > 8) return false;

    return hasSvg || /Icon|Logo|Badge|Thumbnail|Image|Mark/i.test(className);
  }

  function findDeliveryRow(clickable) {
    var rows = clickable.querySelectorAll('.InlineStack__InlineStack');
    var best = null;

    for (var i = 0; i < rows.length; i++) {
      var row = rows[i];
      var text = (row.textContent || '').toLowerCase();
      if (
        text.indexOf('free') === -1 &&
        text.indexOf('delivery') === -1 &&
        text.indexOf('arrives') === -1 &&
        text.indexOf('ship') === -1
      ) {
        continue;
      }

      if (!best || row.children.length < best.children.length) {
        best = row;
      }
    }

    if (best) return best;

    var stack = clickable.querySelector('.BlockStack__BlockStack');
    if (stack) {
      var stacked = stack.querySelector(':scope > .InlineStack__InlineStack');
      if (stacked) return stacked;
    }

    return clickable.querySelector('.InlineStack__InlineStack');
  }

  function applyRowGap(row) {
    row.style.setProperty('display', 'flex', 'important');
    row.style.setProperty('align-items', 'center', 'important');
    row.style.setProperty('column-gap', ROW_GAP, 'important');
    row.style.setProperty('--ui-inline-stack-column-gap', ROW_GAP, 'important');
  }

  function ensureToggleMarkup(toggle) {
    var svg = toggle.querySelector('svg');
    if (!svg || toggle.dataset.ezIcon !== 'pin-green') {
      if (svg) svg.remove();
      toggle.insertAdjacentHTML('afterbegin', PIN_SVG);
      toggle.dataset.ezIcon = 'pin-green';
    }

    var pad = toggle.querySelector('.' + TOGGLE_PAD_CLASS);
    if (!pad) {
      pad = document.createElement('span');
      pad.className = TOGGLE_PAD_CLASS;
      pad.setAttribute('aria-hidden', 'true');
      toggle.appendChild(pad);
    }

    toggle.style.setProperty('display', 'inline-flex', 'important');
    toggle.style.setProperty('align-items', 'center', 'important');
    toggle.style.setProperty('flex', '0 0 auto', 'important');
    toggle.style.setProperty('color', 'rgb(var(--ez-color-primary, 123, 157, 73))', 'important');
    toggle.style.setProperty('margin', '0', 'important');
    toggle.style.setProperty('padding', '0', 'important');

    pad.style.setProperty('display', 'inline-block', 'important');
    pad.style.setProperty('width', ROW_GAP, 'important');
    pad.style.setProperty('min-width', ROW_GAP, 'important');
    pad.style.setProperty('flex', '0 0 ' + ROW_GAP, 'important');
    pad.style.setProperty('height', '1px', 'important');
  }

  function removeDefaultRowIcons(row, toggle) {
    var copyText = (row.textContent || '').replace(/\s+/g, ' ').trim();
    if (copyText.length < 8) return;

    var contentChildren = [];
    row.querySelectorAll(':scope > *').forEach(function (child) {
      if (child === toggle || child.classList.contains(TOGGLE_CLASS)) return;
      contentChildren.push(child);
    });
    if (contentChildren.length < 2) return;

    contentChildren.forEach(function (child) {
      if (!isDefaultRowIcon(child, toggle)) return;
      child.style.setProperty('display', 'none', 'important');
    });
  }

  function tagCopyBlock(row, toggle) {
    row.querySelectorAll(':scope > *').forEach(function (child) {
      child.classList.remove(COPY_CLASS);
    });

    var children = row.children;
    for (var i = 0; i < children.length; i++) {
      var child = children[i];
      if (child === toggle || child.classList.contains(TOGGLE_CLASS)) continue;
      child.classList.add(COPY_CLASS);
      return;
    }
  }

  function applyDetailAlign(clickable) {
    var row = findDeliveryRow(clickable);
    if (!row) return;

    function measure() {
      var copy = row.querySelector('.' + COPY_CLASS);
      var stack = row.parentElement;
      if (!copy || !stack || String(stack.className).indexOf('BlockStack') === -1) return;

      var indent = copy.getBoundingClientRect().left - stack.getBoundingClientRect().left;
      if (!indent || indent < 1) return;

      var px = Math.round(indent) + 'px';
      stack.style.setProperty('--ez-shop-promise-text-indent', px);
      clickable.style.setProperty('--ez-shop-promise-text-indent', px);

      for (var i = 0; i < stack.children.length; i++) {
        var child = stack.children[i];
        if (child === row) continue;
        child.classList.add(DETAIL_CLASS);
        child.style.setProperty('padding-inline-start', px, 'important');
        child.style.setProperty('box-sizing', 'border-box', 'important');
      }
    }

    measure();
    requestAnimationFrame(measure);
  }

  function bindDetailAlign(clickable) {
    if (clickable.dataset.ezAlignBound === 'true') return;
    clickable.dataset.ezAlignBound = 'true';

    clickable.addEventListener('click', function () {
      requestAnimationFrame(function () {
        applyDetailAlign(clickable);
      });
      setTimeout(function () {
        applyDetailAlign(clickable);
      }, 220);
    });
  }

  function hideInputRowIcons(root) {
    root.querySelectorAll('input').forEach(function (input) {
      var row =
        input.closest('[class*="InlineStack"]') ||
        input.closest('[class*="Stack"]') ||
        input.parentElement;
      if (!row) return;

      Array.from(row.children).forEach(function (child) {
        if (child.contains(input) || child.tagName === 'INPUT') return;
        if (child.tagName === 'BUTTON' || child.querySelector('button')) return;

        var className = String(child.className || '');
        var isIcon =
          child.tagName === 'SVG' ||
          !!child.querySelector('svg') ||
          /icon|prefix|pin|logo|mark/i.test(className);

        if (!isIcon) return;

        child.style.setProperty('display', 'none', 'important');
        child.style.setProperty('width', '0', 'important');
        child.style.setProperty('min-width', '0', 'important');
        child.style.setProperty('height', '0', 'important');
        child.style.setProperty('overflow', 'hidden', 'important');
        child.style.setProperty('margin', '0', 'important');
        child.style.setProperty('padding', '0', 'important');
      });
    });
  }

  function hideGuaranteeCopy(root) {
    root.querySelectorAll('p, span, div').forEach(function (el) {
      var text = (el.textContent || '').replace(/\s+/g, ' ').trim();
      if (!/^shop promise guarantees fast/i.test(text)) return;
      if (!/shop cash/i.test(text)) return;
      el.setAttribute('data-ez-hide-guarantee', 'true');
      el.style.setProperty('display', 'none', 'important');
    });
  }

  function decorate(root) {
    hideGuaranteeCopy(root);
    hideInputRowIcons(root);

    var clickables = root.querySelectorAll('.ShopPromiseLayout__Clickable');
    if (!clickables.length) {
      clickables = root.querySelectorAll('[class*="Clickable"]');
    }

    clickables.forEach(function (clickable) {
      var row = findDeliveryRow(clickable);
      if (!row) return;

      row.classList.add(ROW_CLASS);
      applyRowGap(row);

      var toggle = row.querySelector(':scope > .' + TOGGLE_CLASS);
      if (!toggle) {
        toggle = document.createElement('span');
        toggle.className = TOGGLE_CLASS;
        toggle.setAttribute('aria-hidden', 'true');
        toggle.innerHTML = PIN_SVG;
        toggle.dataset.ezIcon = 'pin-green';
        row.insertBefore(toggle, row.firstChild);
      }

      removeDefaultRowIcons(row, toggle);
      ensureToggleMarkup(toggle);
      tagCopyBlock(row, toggle);
      applyDetailAlign(clickable);
      bindDetailAlign(clickable);
    });
  }

  function inject(el, attempt) {
    var root = el.shadowRoot;
    if (!root) {
      if ((attempt || 0) < 60) {
        requestAnimationFrame(function () {
          inject(el, (attempt || 0) + 1);
        });
      }
      return;
    }

    var style = root.getElementById(STYLE_ID);
    if (!style) {
      style = document.createElement('style');
      style.id = STYLE_ID;
      root.appendChild(style);
    }
    style.textContent = CSS;

    decorate(root);

    if (!root.__ezShopPromiseObserved) {
      root.__ezShopPromiseObserved = true;
      new MutationObserver(function () {
        decorate(root);
      }).observe(root, { childList: true, subtree: true });
    }
  }

  function unwrapLightPin(el) {
    var host = el.parentElement;
    if (!host || !host.classList.contains('ez-shop-promise-host')) return;
    var parent = host.parentElement;
    if (!parent) return;
    parent.insertBefore(el, host);
    host.remove();
  }

  var scanTimer = 0;
  function scan() {
    window.clearTimeout(scanTimer);
    scanTimer = window.setTimeout(function () {
      document.querySelectorAll('delivery-promise-wc').forEach(function (el) {
        unwrapLightPin(el);
        inject(el);
      });
    }, 50);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }

  new MutationObserver(scan).observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
})();
